import type { ComponentProps, ReactNode } from 'react';

import type { Placement } from '@floating-ui/react';
import type { ColorName } from 'styles/config/colors';

import type { ComponentWithChildren } from '@polyai/common/types/helpers';
import ConditionalWrapper from '@polyai/common/utils/conditionalWrapper';

import { cn } from 'utils/cn';
import Tooltip from '../Tooltip';

export interface HelpTextProps {
  help?: ReactNode;
  portal?: boolean;
  autoUpdateOptions?: ComponentProps<typeof Tooltip>['autoUpdateOptions'];
  inline?: boolean;
  backgroundColor?: ColorName;
  'data-test-id'?: string;
  className?: string;
  placement?: Placement;
}

const HelpText: ComponentWithChildren<HelpTextProps> = ({
  children,
  help,
  portal = false,
  autoUpdateOptions,
  inline,
  backgroundColor,
  'data-test-id': dataTestId = 'help-text',
  className,
  placement = 'top-start',
}) => {
  return (
    <ConditionalWrapper
      condition={!!help}
      wrapper={children => (
        <div className={cn(inline ? 'inline-flex' : 'flex')}>
          <Tooltip
            autoUpdateOptions={autoUpdateOptions}
            backgroundColor={backgroundColor}
            className={className}
            content={help}
            data-test-id={dataTestId}
            placement={placement}
            portal={portal}
          >
            <div
              className={cn(
                'cursor-pointer underline decoration-dashed',
                'decoration-border-strong underline-offset-xxs2',
              )}
              data-test-id="help-text-label"
              role="tooltip"
            >
              {children}
            </div>
          </Tooltip>
        </div>
      )}
    >
      <span>{children}</span>
    </ConditionalWrapper>
  );
};

export default HelpText;
